/**
 * Outbound participation sheet (#27 E, Josh 09/29) — PFS taking a % of another funder's deal
 * (lettered deals). Enter their deal specs → the five-line sheet → "Total Owed From PFS", checked
 * against what they asked for, then "Wire sent". Never touches the ledger or the deal's own
 * funding/payback (which already hold PFS's slice).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Handshake } from 'lucide-react';
import { formatCurrency, formatDate } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const isLettered = (id) => /^[A-Za-z]/.test(String(id || ''));
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };

const blank = () => ({ platform_contact_id: '', their_funding: '', their_factor: '', their_payback: '', origination_fee: '',
  origination_fee_is_pct: true, broker_commission: '', broker_commission_is_pct: true, pfs_pct: '', management_fee_pct: '2',
  subject_to_fees: false, collects_origination: true, their_request_amount: '', notes: '' });

const ParticipationCard = ({ deal, getAuthHeaders, onRefresh }) => {
  const [part, setPart] = useState(deal?.participation || null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(blank());
  const [preview, setPreview] = useState(null);
  const [funders, setFunders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [wireOpen, setWireOpen] = useState(false);
  const [wire, setWire] = useState({ date: new Date().toISOString().slice(0, 10), amount: '', reference: '' });

  useEffect(() => { setPart(deal?.participation || null); }, [deal?.participation]);
  useEffect(() => {
    if (!editing) return;
    axios.get(`${API}/contacts?limit=500`, { headers: getAuthHeaders() })
      .then(r => { const rows = Array.isArray(r.data) ? r.data : (r.data?.contacts || []); setFunders(rows.filter(c => c.is_funder || c.is_partner || c.is_investor)); })
      .catch(() => {});
  }, [editing, getAuthHeaders]);

  const body = useCallback(() => ({
    platform_contact_id: form.platform_contact_id || null,
    their_funding: num(form.their_funding), their_factor: num(form.their_factor), their_payback: num(form.their_payback),
    origination_fee: num(form.origination_fee) || 0, origination_fee_is_pct: form.origination_fee_is_pct,
    broker_commission: num(form.broker_commission) || 0, broker_commission_is_pct: form.broker_commission_is_pct,
    pfs_pct: num(form.pfs_pct), management_fee_pct: num(form.management_fee_pct) ?? 2,
    subject_to_fees: form.subject_to_fees, collects_origination: form.collects_origination,
    their_request_amount: num(form.their_request_amount), notes: form.notes,
  }), [form]);

  // live preview
  useEffect(() => {
    if (!editing) return undefined;
    const b = body();
    if (!b.their_funding || !b.pfs_pct || (!b.their_factor && !b.their_payback)) { setPreview(null); return undefined; }
    const t = setTimeout(() => {
      axios.post(`${API}/deals/participation/preview`, b, { headers: getAuthHeaders() }).then(r => setPreview(r.data)).catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(t);
  }, [editing, body, getAuthHeaders]);

  const startEdit = () => {
    const i = part?.inputs;
    setForm(i ? {
      platform_contact_id: part.platform_contact_id || '', their_funding: String(i.their_funding ?? ''), their_factor: String(i.their_factor ?? ''),
      their_payback: String(i.their_payback ?? ''), origination_fee: String(i.origination_fee_total ?? ''), origination_fee_is_pct: false,
      broker_commission: String(i.broker_commission_total ?? ''), broker_commission_is_pct: false, pfs_pct: String(i.pfs_pct ?? ''),
      management_fee_pct: String(i.management_fee_pct ?? 2), subject_to_fees: !!i.subject_to_fees, collects_origination: i.collects_origination !== false,
      their_request_amount: String(part.their_request_amount ?? ''), notes: part.notes || '',
    } : blank());
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/deals/${deal.id}/participation`, body(), { headers: getAuthHeaders() });
      setPart(r.data.participation); setEditing(false);
      toast.success(`Sheet saved — total owed from PFS ${formatCurrency(r.data.participation.total_owed_from_pfs)}`);
      if (r.data.warning) toast.warning(r.data.warning, { duration: 8000 });
      if (onRefresh) onRefresh();
    } catch (e) {
      const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Could not save');
    } finally { setBusy(false); }
  };

  const sendWire = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/deals/${deal.id}/participation/wire-sent`, { date: wire.date, amount: num(wire.amount), reference: wire.reference }, { headers: getAuthHeaders() });
      toast.success('Wire recorded'); setWireOpen(false); if (onRefresh) onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record'); } finally { setBusy(false); }
  };

  if (!part && !isLettered(deal?.deal_id) && !editing) return null;   // only lettered deals or deals that already have a sheet

  const sheet = editing ? preview : part;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Card data-testid="participation-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Handshake className="h-4 w-4" /> Participation — PFS into {part?.platform_name || 'another funder'}'s deal
            {part?.wire_sent_at && <Badge variant="secondary">wire sent {formatDate(part.wire_sent_at)}</Badge>}
            {part && !part.wire_sent_at && <Badge variant="destructive">wire not sent</Badge>}
          </CardTitle>
          <div className="flex gap-2">
            {!editing && <Button size="sm" variant="outline" onClick={startEdit} data-testid="participation-edit">{part ? 'Edit sheet' : 'Set up sheet'}</Button>}
            {!editing && part && !part.wire_sent_at && <Button size="sm" onClick={() => { setWire(w => ({ ...w, amount: String(part.total_owed_from_pfs) })); setWireOpen(true); }} data-testid="participation-wire-btn">Wire sent</Button>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {editing && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Their platform (funder)</Label>
              <Select value={form.platform_contact_id || '__none__'} onValueChange={v => set('platform_contact_id', v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Pick contact" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">— not set —</SelectItem>{funders.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Their funding ($)</Label><Input type="number" step="0.01" value={form.their_funding} onChange={e => set('their_funding', e.target.value)} data-testid="part-their-funding" /></div>
            <div><Label className="text-xs">PFS %</Label><Input type="number" step="0.01" value={form.pfs_pct} onChange={e => set('pfs_pct', e.target.value)} data-testid="part-pfs-pct" /></div>
            <div><Label className="text-xs">Their factor</Label><Input type="number" step="0.0001" value={form.their_factor} onChange={e => set('their_factor', e.target.value)} placeholder="1.40" /></div>
            <div><Label className="text-xs">or their payback ($)</Label><Input type="number" step="0.01" value={form.their_payback} onChange={e => set('their_payback', e.target.value)} /></div>
            <div>
              <Label className="text-xs">Origination fee {form.origination_fee_is_pct ? '(%)' : '($)'} <button className="text-indigo-600 underline ml-1" onClick={() => set('origination_fee_is_pct', !form.origination_fee_is_pct)}>{form.origination_fee_is_pct ? 'use $' : 'use %'}</button></Label>
              <Input type="number" step="0.01" value={form.origination_fee} onChange={e => set('origination_fee', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Their broker commission {form.broker_commission_is_pct ? '(%)' : '($)'} <button className="text-indigo-600 underline ml-1" onClick={() => set('broker_commission_is_pct', !form.broker_commission_is_pct)}>{form.broker_commission_is_pct ? 'use $' : 'use %'}</button></Label>
              <Input type="number" step="0.01" value={form.broker_commission} onChange={e => set('broker_commission', e.target.value)} />
            </div>
            <div className="flex items-center gap-2 pt-5"><Switch checked={form.collects_origination} onCheckedChange={v => set('collects_origination', v)} /><span className="text-xs">PFS collects on origination fee</span></div>
            <div className="flex items-center gap-2 pt-5"><Switch checked={form.subject_to_fees} onCheckedChange={v => set('subject_to_fees', v)} /><span className="text-xs">Subject to their management fee</span></div>
            {form.subject_to_fees && <div><Label className="text-xs">Their mgmt fee %</Label><Input type="number" step="0.01" value={form.management_fee_pct} onChange={e => set('management_fee_pct', e.target.value)} /></div>}
            <div><Label className="text-xs">Amount they requested ($)</Label><Input type="number" step="0.01" value={form.their_request_amount} onChange={e => set('their_request_amount', e.target.value)} placeholder="tie-out" /></div>
            <div className="col-span-2 md:col-span-4"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
          </div>
        )}

        {sheet ? (
          <div className="rounded-lg border bg-muted/30 p-3 max-w-md" data-testid="participation-sheet">
            {sheet.lines.map(l => (
              <div key={l.key} className={`flex justify-between py-0.5 ${l.applies === false ? 'text-muted-foreground line-through' : ''}`}>
                <span>{l.sign === '-' ? '− ' : '+ '}{l.label}</span><span className="font-mono">{formatCurrency(l.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t mt-1 pt-1 font-bold"><span>Total Owed From PFS</span><span className="font-mono" data-testid="participation-total">{formatCurrency(sheet.total_owed_from_pfs)}</span></div>
            {sheet.their_request_amount != null && (
              <div className={`text-xs mt-1 ${Math.abs(sheet.tie_out_diff || 0) > 0.005 ? 'text-amber-700' : 'text-green-700'}`}>
                They requested {formatCurrency(sheet.their_request_amount)} — {Math.abs(sheet.tie_out_diff || 0) > 0.005 ? `off by ${formatCurrency(sheet.tie_out_diff)}` : 'ties out'}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-2">PFS payback {formatCurrency(sheet.pfs_payback)} on capital {formatCurrency(sheet.pfs_capital)} → expected profit {formatCurrency(sheet.expected_profit)}{sheet.inputs?.their_payback ? ` · their RTR ${formatCurrency(sheet.inputs.their_payback)}` : ''}</div>
            {part?.wire_sent_at && !editing && <div className="text-xs mt-1">Wired {formatCurrency(part.wire_amount)} on {formatDate(part.wire_sent_at)}{part.wire_reference ? ` · ${part.wire_reference}` : ''}</div>}
          </div>
        ) : (editing ? <p className="text-xs text-muted-foreground">Enter their funding, PFS % and factor (or payback) to see the sheet.</p> : null)}

        {editing && (
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={busy || !preview} data-testid="participation-save">Save sheet</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
          </div>
        )}
      </CardContent>

      {wireOpen && (
        <Dialog open onOpenChange={(v) => !v && setWireOpen(false)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Wire sent to {part?.platform_name || 'the platform'}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Date</Label><Input type="date" value={wire.date} onChange={e => setWire(w => ({ ...w, date: e.target.value }))} /></div>
              <div><Label className="text-xs">Amount</Label><Input type="number" step="0.01" value={wire.amount} onChange={e => setWire(w => ({ ...w, amount: e.target.value }))} /></div>
              <div className="col-span-2"><Label className="text-xs">Reference</Label><Input value={wire.reference} onChange={e => setWire(w => ({ ...w, reference: e.target.value }))} placeholder="wire confirmation #" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setWireOpen(false)} disabled={busy}>Cancel</Button>
              <Button onClick={sendWire} disabled={busy}>Record</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

export default ParticipationCard;
